import { Fragment, forwardRef } from 'react';

import { TableCell, TableCellProps } from '@material-ui/core';

import { isSameDay } from 'date-fns';
import * as duration from 'duration-fns';

import {
  MetadataText,
  formatDateFuture,
  formatDuration,
} from '@spotify/confidence-core-react';
import {
  RolloutDataScheduleScheduleStepsStatus,
  RolloutDataScheduleState,
  RolloutListItemFragment,
} from '@spotify/confidence-graphql';
import {
  DraftReadinessText,
  EndedRuntimeText,
  usePlanningDates,
} from '@spotify/plugin-confidence-workflows-module-planning';
import { ProgressBar } from '@spotify/plugin-confidence-workflows-react';

interface ProgressCellProps extends TableCellProps {
  workflowInstance: Pick<
    RolloutListItemFragment,
    'checks' | 'moduleData2' | 'state'
  >;
}

export const ProgressCell = forwardRef<HTMLTableCellElement, ProgressCellProps>(
  ({ workflowInstance, ...props }, ref) => {
    const { state, checks, moduleData2 } = workflowInstance;
    const { plannedStartDate, actualRuntime } = usePlanningDates(
      moduleData2.planning,
    );

    return (
      <TableCell {...props} ref={ref}>
        {state === 'draft' && (
          <DraftReadinessText
            checks={checks}
            plannedStartDate={plannedStartDate}
          />
        )}
        {state === 'live' && (
          <LiveProgress workflowInstance={workflowInstance} />
        )}
        {(state === 'ended' || (state === 'archived' && actualRuntime)) && (
          <EndedRuntimeText label="Finished" actualRuntime={actualRuntime} />
        )}
        {state === 'archived' && !actualRuntime && (
          <MetadataText items={['Archived']} />
        )}
      </TableCell>
    );
  },
);

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

function LiveProgress({
  workflowInstance,
}: {
  workflowInstance: Pick<RolloutListItemFragment, 'moduleData2'>;
}) {
  const {
    rollout,
    exposure: exposureData,
    planning,
  } = workflowInstance.moduleData2;
  const { actualStartDate, currentRuntime } = usePlanningDates(planning);

  const schedule = rollout?.schedule;
  const scheduleSteps = schedule?.steps ?? [];

  const exposure = exposureData?.exposure?.reduce(
    (acc, curr) => acc + (Number(curr.totalExposureCount) || 0),
    0,
  );

  const progress = rollout?.reach ? Number(rollout.reach) * 100 : null;

  const nextIndex = scheduleSteps.findIndex(
    step => step.status !== RolloutDataScheduleScheduleStepsStatus.Succeeded,
  );

  const prevStep = nextIndex > 0 ? scheduleSteps[nextIndex - 1] : null;

  const prevReach =
    prevStep?.status === RolloutDataScheduleScheduleStepsStatus.Succeeded
      ? Number(prevStep.reach) * 100
      : null;

  const nextStep = nextIndex !== -1 ? scheduleSteps[nextIndex] : null;

  const nextReach =
    nextStep?.status === RolloutDataScheduleScheduleStepsStatus.Pending
      ? Number(nextStep.reach) * 100
      : null;

  const scheduleRuntime = scheduleSteps
    .slice(0, nextIndex + 1)
    .reduce(
      (acc, step) =>
        duration.sum(acc, duration.parse(step.durationSincePreviousStep)),
      {},
    );

  const nextDate = actualStartDate
    ? duration.apply(actualStartDate, scheduleRuntime)
    : null;

  const isAutomaticallyFailed =
    schedule?.state === RolloutDataScheduleState.Failed &&
    progress !== null &&
    progress === prevReach;

  const renderText = () => {
    if (
      nextDate &&
      nextReach &&
      schedule?.state === RolloutDataScheduleState.Enabled &&
      isSameDay(nextDate, new Date())
    ) {
      return `${nextReach}% ${formatDateFuture(nextDate)}`;
    }

    if (isAutomaticallyFailed) {
      return 'Schedule stopped';
    }

    if (exposure !== undefined) {
      if (exposure === 0) return 'None exposed yet';
      return `${compactFormatter.format(exposure)} exposed`;
    }

    return null;
  };

  return (
    <Fragment>
      {progress !== null && (
        <Fragment>
          <ProgressBar
            variant="determinate"
            value={progress}
            color="secondary"
          />
          <MetadataText items={[`${progress}%`, renderText()]} />
        </Fragment>
      )}

      {progress === null && (
        <MetadataText items={[formatDuration(currentRuntime)]} />
      )}
    </Fragment>
  );
}
